import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { sessionStorageKeys, tqQueryKeys } from "lib/constants";
import { EvendyApi } from "lib/utils/api-utils";
import { ISODateString } from "lib/types";

type User = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  userType: string;
  verified: boolean;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  lastLogin: ISODateString;
};

export type VendorData = {
  id: number;
  vendorEmail: string;
  businessName: string | null;
  businessCategoryName: string | null;
  description: string | null;
  phone: string | null;
  avatar: string | null;
  coverImage: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
  address: string | null;
  workAddress: string | null;
  kycLevel: string;
  role: string;
  accountStatus: string;
  deletedAt: ISODateString | null;
  createdAt: ISODateString;
  updatedAt: ISODateString;
  user: User;
  requests: object[];
  reviews: object[];
};

type VendorResponse = {
  data: VendorData[];
  page: number;
  limit: number;
  totalCount: number;
};

type VendorResult = UseQueryResult<VendorResponse, Error>;

export type GetVendorPayload = {
  page: number;
  limit: number;
  search?: string;
  location?: string;
  minCompletedRequests?: number;
  minRating?: number;
  category?: string;
};

export function useGetVendor(payload: GetVendorPayload): VendorResult {
  const { page, limit, search, location, minCompletedRequests, minRating, category } = payload;

  // Get access token from sessionStorage
  const getAccessToken = (): string | null => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem(sessionStorageKeys.accessToken);
    }
    return null;
  };

  const accessToken = getAccessToken();

  // Filter out undefined or null params
  const params = {
    page,
    limit,
    ...(search && { search }),
    ...(location && { location }),
    ...(minCompletedRequests && { minCompletedRequests }),
    ...(minRating && { minRating }),
    ...(category && { category }),
  };

  return useQuery({
    queryKey: [
      tqQueryKeys.vendors.getVendors,
      ...Object.values(params),
      page,
      limit,
      search,
      location,
      minCompletedRequests,
      minRating,
      category,
      accessToken, // Include accessToken in queryKey
    ],
    queryFn: async (): Promise<VendorResponse> => {
      if (!accessToken) {
        throw new Error("No access token found in sessionStorage");
      }

      try {
        const res = await EvendyApi.get<VendorResponse>("vendor", {
          params,
          headers: {
            Authorization: `Bearer ${accessToken}`,
            accept: "application/json",
          },
        });
        return res.data;
      } catch (error) {
        console.error("API Error:", error);
        throw error;
      }
    },
    enabled: !!page && !!limit && !!accessToken,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401) return false;
      return failureCount < 3;
    },
  });
}

// import { useQuery, UseQueryResult } from "@tanstack/react-query";
// import { sessionStorageKeys, tqQueryKeys } from "lib/constants";
// import { EvendyApi } from "lib/utils/api-utils";
// import { ISODateString } from "ui/types";

// type User = {
//   id: number;
//   firstName: string;
//   lastName: string;
//   email: string;
//   phone: string;
//   userType: string;
//   verified: boolean;
//   createdAt: ISODateString;
//   updatedAt: ISODateString;
//   lastLogin: ISODateString;
// }

// export type VendorData = {
//   id: number;
//   vendorEmail: string;
//   businessName: string | null;
//   businessCategoryName: string | null;
//   description: string | null;
//   phone: string | null;
//   avatar: string | null;
//   coverImage: string | null;
//   country: string | null;
//   state: string | null;
//   city: string | null;
//   address: string | null;
//   workAddress: string | null;
//   kycLevel: string;
//   role: string;
//   accountStatus: string;
//   deletedAt: ISODateString | null;
//   createdAt: ISODateString;
//   updatedAt: ISODateString;
//   user: User;
//   requests: object[];
//   reviews: object[];
// }

// type VendorResponse = {
//   data: VendorData[];
//   page: number;
//   limit: number;
//   totalCount: number;
// }

// type VendorResult = UseQueryResult<VendorResponse, Error>;

// export type GetVendorPayload = {
//   page: number;
//   limit: number;
//   search?: string;
//   location?: string;
//   minCompletedRequests?: number;
//   minRating?: number;
//   category?: string;
// };

// export function useGetVendor(payload: GetVendorPayload): VendorResult {
//   const {
//     page,
//     limit,
//     search,
//     location,
//     minCompletedRequests,
//     minRating,
//     category
//   } = payload;

//   // Get API key from sessionStorage (same as useAdminLogin)
//   const getApiKey = (): string | null => {
//     if (typeof window !== "undefined") {
//       return sessionStorage.getItem("authCredentials");
//     }
//     return null;
//   };

//   const apiKey = getApiKey();

//   // Filter out undefined or null params
//   const params = {
//     page,
//     limit,
//     ...(search && { search }),
//     ...(location && { location }),
//     ...(minCompletedRequests && { minCompletedRequests }),
//     ...(minRating && { minRating }),
//     ...(category && { category }),
//   };

//   return useQuery({
//     queryKey: [
//       tqQueryKeys.vendors.getVendors,
//       ...Object.values(params),
//       page,
//       limit,
//       search,
//       location,
//       minCompletedRequests,
//       minRating,
//       category,
//       apiKey, // Include apiKey in queryKey
//     ],
//     queryFn: async (): Promise<VendorResponse> => {
//       if (!apiKey) {
//         throw new Error("No API key found in sessionStorage");
//       }

//       try {
//         const res = await EvendyApi.get<VendorResponse>("vendor", {
//           params,
//           headers: {
//             "x-api-key": apiKey,
//             accept: "application/json"
//           }
//         });
//         return res.data;
//       } catch (error) {
//         console.error("API Error:", error);
//         throw error;
//       }
//     },
//     enabled: !!page && !!limit && !!apiKey,
//     retry: (failureCount, error: any) => {
//       if (error?.response?.status === 401) return false;
//       return failureCount < 3;
//     },
//   });
// }
